import {
  addDays,
  compareAsc,
  differenceInCalendarDays,
  endOfDay,
  format,
  getMonth,
  getYear,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns'

export type DateLike = Date | string | number
export type IsoDateLike = DateLike | { toISOString(): string }
export type DurationUnit = 'seconds' | 'minutes' | 'hours' | 'days'

/**
 * Minimal duration contract used by the formatting helpers in this module.
 */
export interface Duration {
  days(): number
  hours(): number
  minutes(): number
  seconds(): number
  asMilliseconds(): number
  asHours(): number
}

/**
 * English relative-time cutoffs preserved so the migration does not change
 * visible copy in existing screens.
 */
const relativeTimeThresholds = {
  ss: 44,
  m: 45,
  h: 22,
  d: 26,
  M: 11,
} as const

/**
 * Converts days to months using the same Gregorian-average ratio as the
 * previous implementation instead of a fixed 30/31-day divisor.
 */
function daysToRelativeMonths(days: number): number {
  return (days * 4800) / 146097
}

/**
 * Recreates the old formatting entry behavior without a date-library wrapper.
 *
 * These helpers accept more than the declared `DateLike` contract:
 * `undefined` meant "now", `null` produced an invalid date sentinel, and
 * wrapper objects still work because they expose a primitive `valueOf()`.
 * Existing callers still rely on that behavior, so we normalize those inputs
 * explicitly before handing off to `date-fns`.
 */
function toFormattingDate(value: unknown): Date {
  if (value === undefined) {
    return new Date()
  }

  if (value === null) {
    return new Date(Number.NaN)
  }

  if (value instanceof Date || typeof value === 'string' || typeof value === 'number') {
    return toDate(value)
  }

  if (typeof value === 'object' && 'valueOf' in value && typeof value.valueOf === 'function') {
    const primitive = value.valueOf()

    if (primitive !== value) {
      return toFormattingDate(primitive)
    }
  }

  return new Date(value as string | number | Date)
}

/**
 * Centralizes the formatter contract shared by the compatibility helpers.
 *
 * The exported formatting helpers all shared the same "format when valid,
 * otherwise return `Invalid date`" rule. Keeping it here prevents subtle
 * drift between `formatDateTime`, `formatDateWithYear`, and `formatTimeRange`.
 */
function formatDateValue(value: unknown, pattern: string): string {
  const date = toFormattingDate(value)
  return isValid(date) ? format(date, pattern) : 'Invalid date'
}

/**
 * Reimplements default English relative-time phrasing.
 *
 * We keep the old rounding model, month conversion, and threshold ordering so
 * the migration does not change visible copy like "an hour ago" vs
 * "2 hours ago" or "a month ago" vs "30 days ago".
 */
function formatRelativeTime(date: Date, now = new Date()): string {
  if (!isValid(date)) {
    return 'Invalid date'
  }

  const diffMs = date.getTime() - now.getTime()
  const absMs = Math.abs(diffMs)
  const seconds = Math.round(absMs / 1000)
  const minutes = Math.round(absMs / 60000)
  const hours = Math.round(absMs / 3600000)
  const days = Math.round(absMs / 86400000)
  const months = Math.round(daysToRelativeMonths(days))
  const years = Math.round(months / 12)

  let phrase: string

  if (seconds <= relativeTimeThresholds.ss) {
    phrase = 'a few seconds'
  } else if (minutes <= 1) {
    phrase = 'a minute'
  } else if (minutes < relativeTimeThresholds.m) {
    phrase = `${minutes} minutes`
  } else if (hours <= 1) {
    phrase = 'an hour'
  } else if (hours < relativeTimeThresholds.h) {
    phrase = `${hours} hours`
  } else if (days <= 1) {
    phrase = 'a day'
  } else if (days < relativeTimeThresholds.d) {
    phrase = `${days} days`
  } else if (months <= 1) {
    phrase = 'a month'
  } else if (months < relativeTimeThresholds.M) {
    phrase = `${months} months`
  } else if (years <= 1) {
    phrase = 'a year'
  } else {
    phrase = `${years} years`
  }

  return diffMs > 0 ? `in ${phrase}` : `${phrase} ago`
}

/**
 * Normalizes a supported date-like input into a `Date` instance.
 *
 * ISO-style date strings are parsed with `parseISO` first so date-only strings
 * stay on their intended local calendar day.
 *
 * @param value Date, string, or timestamp to normalize.
 * @returns A `Date` instance representing the same point in time.
 */
export function toDate(value: DateLike): Date {
  if (value instanceof Date) {
    return value
  }

  if (typeof value === 'string') {
    const parsed = parseISO(value)
    return Number.isNaN(parsed.getTime()) ? new Date(value) : parsed
  }

  return new Date(value)
}

/**
 * Formats a date-like value as an ISO-style calendar date.
 *
 * @param value Date-like value to format.
 * @returns A string in `yyyy-MM-dd` format.
 */
export function formatIsoDate(value: DateLike): string {
  const d = toDate(value)
  if (!isValid(d)) return 'Invalid date'
  return format(d, 'yyyy-MM-dd')
}

/**
 * Formats a supported date-like input as an ISO timestamp string.
 *
 * Objects that already expose `toISOString()` are used directly so Moment-like
 * values can be normalized without a runtime dependency on Moment.
 *
 * @param value Date-like value to normalize.
 * @returns An ISO 8601 timestamp string, or `Invalid date` for invalid inputs.
 */
export function toIsoString(value: IsoDateLike): string {
  if (typeof value === 'object' && value !== null && 'toISOString' in value) {
    try {
      const isoString = value.toISOString()
      return isValid(new Date(isoString)) ? isoString : 'Invalid date'
    } catch {
      return 'Invalid date'
    }
  }

  const date = toDate(value)
  return isValid(date) ? date.toISOString() : 'Invalid date'
}

/**
 * Returns the local start-of-day boundary for a date-like value.
 *
 * @param value Date-like value to normalize.
 * @returns A `Date` at the start of the local calendar day.
 */
export function startOfLocalDay(value: DateLike): Date {
  return startOfDay(toDate(value))
}

/**
 * Returns the local end-of-day boundary for a date-like value.
 *
 * @param value Date-like value to normalize.
 * @returns A `Date` at the end of the local calendar day.
 */
export function endOfLocalDay(value: DateLike): Date {
  return endOfDay(toDate(value))
}

/**
 * Returns the first instant of the month for a date-like value in local time.
 *
 * @param value Date-like value to normalize.
 * @returns A `Date` at the start of the local calendar month.
 */
export function startOfLocalMonth(value: DateLike): Date {
  return startOfMonth(toDate(value))
}

/**
 * Adds a whole-number day offset to a date-like value.
 *
 * @param value Base date-like value.
 * @param days Number of days to add. Negative values subtract days.
 * @returns A new `Date` with the day offset applied.
 */
export function addCalendarDays(value: DateLike, days: number): Date {
  return addDays(toDate(value), days)
}

/**
 * Subtracts a whole-number month offset from a date-like value.
 *
 * @param value Base date-like value.
 * @param months Number of months to subtract.
 * @returns A new `Date` with the month offset applied.
 */
export function subtractCalendarMonths(value: DateLike, months: number): Date {
  return subMonths(toDate(value), months)
}

/**
 * Returns the calendar-day difference between two date-like values.
 *
 * @param end Ending date-like value.
 * @param start Starting date-like value.
 * @returns The signed number of calendar days between `end` and `start`.
 */
export function diffCalendarDays(end: DateLike, start: DateLike): number {
  return differenceInCalendarDays(toDate(end), toDate(start))
}

/**
 * Compares two date-like values using standard sort semantics.
 *
 * Returns `-1`, `0`, or `1` depending on whether `left` is before, equal to,
 * or after `right`.
 *
 * @param left First date-like value.
 * @param right Second date-like value.
 * @returns `-1`, `0`, or `1` based on the relative ordering.
 */
export function compareDates(left: DateLike, right: DateLike): number {
  return compareAsc(toDate(left), toDate(right))
}

/**
 * Determines whether one date-like value is earlier than another.
 *
 * @param left Candidate earlier date-like value.
 * @param right Candidate later date-like value.
 * @returns `true` when `left` is before `right`.
 */
export function isBeforeDate(left: DateLike, right: DateLike): boolean {
  return isBefore(toDate(left), toDate(right))
}

/**
 * Determines whether one date-like value is later than another.
 *
 * @param left Candidate later date-like value.
 * @param right Candidate earlier date-like value.
 * @returns `true` when `left` is after `right`.
 */
export function isAfterDate(left: DateLike, right: DateLike): boolean {
  return isAfter(toDate(left), toDate(right))
}

/**
 * Determines whether two date-like values fall on the same local calendar day.
 *
 * @param left First date-like value.
 * @param right Second date-like value.
 * @returns `true` when both values resolve to the same local day.
 */
export function isSameCalendarDay(left: DateLike, right: DateLike): boolean {
  return isSameDay(toDate(left), toDate(right))
}

/**
 * Extracts the calendar year from a date-like value.
 *
 * @param value Date-like value to inspect.
 * @returns The four-digit calendar year.
 */
export function getDateYear(value: DateLike): number {
  return getYear(toDate(value))
}

/**
 * Extracts the zero-based month index from a date-like value.
 *
 * @param value Date-like value to inspect.
 * @returns A zero-based month index where January is `0`.
 */
export function getDateMonth(value: DateLike): number {
  return getMonth(toDate(value))
}

/**
 * Returns the current runtime's resolved IANA timezone identifier.
 *
 * @returns The current environment's timezone, such as `America/Edmonton`.
 */
export function currentTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Formats values using the `MMM DD, HH:mm` contract.
 *
 * `undefined` resolves to the current time; invalid inputs return `Invalid date`.
 */
export function formatDateTime(dateTime: unknown): string {
  return formatDateValue(dateTime, 'MMM dd, HH:mm')
}

/**
 * Formats values using the `MMM DD, YYYY HH:mm` contract.
 *
 * `undefined` resolves to the current time; invalid inputs return `Invalid date`.
 */
export function formatDateTimeWithYear(dateTime: unknown): string {
  return formatDateValue(dateTime, 'MMM dd, yyyy HH:mm')
}

/**
 * Formats the current timestamp as `yyyy-MM-dd-HH-mm-ss`.
 *
 * @returns The current local timestamp formatted for filenames or exports.
 */
export function formatCurrentDateTime(): string {
  return format(new Date(), 'yyyy-MM-dd-HH-mm-ss')
}

/**
 * Formats values using the `MMM DD, YYYY` contract.
 *
 * `undefined` resolves to the current time; invalid inputs return `Invalid date`.
 */
export function formatDateWithYear(date: unknown): string {
  return formatDateValue(date, 'MMM dd, yyyy')
}

/**
 * Formats a date range using the existing branching rules.
 *
 * Truthy `end` values are formatted even when invalid; falsey `end` values are treated as missing.
 */
export function formatTimeRange(
  start: unknown,
  end: unknown,
  delimiter = '-',
  noEndText = '',
): string {
  const startDate = formatDateValue(start, 'MMM dd')
  const endDate = end ? formatDateValue(end, 'MMM dd') : null
  const startTime = formatDateValue(start, 'HH:mm')
  const endTime = end ? formatDateValue(end, 'HH:mm') : null

  if (startDate === endDate) {
    return `${startDate}, ${startTime} ${delimiter} ${endTime}`
  } else if (endDate && endTime) {
    return `${startDate}, ${startTime} ${delimiter} ${endDate}, ${endTime}`
  } else {
    return noEndText
      ? `${startDate}, ${startTime} ${delimiter} ${noEndText}`
      : `${startDate}, ${startTime}`
  }
}

/**
 * Formats a duration into compact human-readable segments such as
 * `3days 12hrs` or `45m 30s`.
 *
 * @param duration Duration-like object to format.
 * @param numberOfTimeSegments Maximum number of time segments to include.
 * @param spaceBetweenValueAndUnit Whether spaces should be added between the values and their units.
 * @returns A compact duration string, or an empty string when no duration is provided.
 */
export function formatDuration(
  duration: Duration | null | undefined,
  numberOfTimeSegments = 2,
  spaceBetweenValueAndUnit = false,
): string {
  if (duration == null) return ''
  const separator = spaceBetweenValueAndUnit ? ' ' : ''

  if (duration.asMilliseconds() === 0) {
    return `0${separator}m 0${separator}s`
  }

  const days = duration.days()
  const hours = duration.hours()
  const minutes = duration.minutes()
  const seconds = duration.seconds()
  const dayUnit = days > 1 ? 'days' : 'day'
  const hourUnit = hours > 1 ? 'hrs' : 'hr'
  return [
    days > 0 ? `${days}${separator}${dayUnit}` : null,
    hours > 0 ? `${hours}${separator}${hourUnit}` : null,
    minutes > 0 ? `${minutes}${separator}m` : null,
    seconds > 0 ? `${seconds}${separator}s` : null,
  ]
    .filter((c) => c !== null)
    .slice(0, numberOfTimeSegments)
    .join(' ')
}

/**
 * Formats a duration as decimal hours, optionally preserving a zero-length
 * duration instead of returning an empty string.
 *
 * @param duration Duration-like object to format.
 * @param showZeroDuration When `true`, zero-length durations format as `0.00 hr`.
 * @returns A decimal hour string, or an empty string when no value should be shown.
 */
export function formatDurationAsHours(
  duration: Duration | null | undefined,
  showZeroDuration = false,
): string {
  if (duration === null || duration === undefined) return ''

  const durationAsHours = Math.round(duration.asHours() * 100) / 100

  if (!showZeroDuration && durationAsHours === 0) return ''

  return `${durationAsHours.toFixed(2)} ${durationAsHours > 1 ? 'hrs' : 'hr'}`
}

/**
 * Formats relative time with the preserved English threshold rules.
 */
export function formatTimeAgo(date: unknown): string {
  return formatRelativeTime(toFormattingDate(date))
}

/**
 * Merges two `[start, end]` timestamp ranges into a single inclusive interval.
 *
 * When both ranges are present, the earliest start and latest end are returned
 * in `yyyy-MM-dd HH:mm:ss` format. Incomplete ranges resolve to an empty array.
 *
 * @param startValue Optional start-time range as `[start, end]`.
 * @param endValue Optional end-time range as `[start, end]`.
 * @returns A merged `[start, end]` range, or an empty array when the inputs are incomplete.
 */
export function mergeInterval(
  startValue: string[] | undefined,
  endValue: string[] | undefined,
): string[] {
  if (!startValue && !endValue) {
    return []
  }

  if (!startValue && endValue && endValue.length >= 2) {
    return [endValue[0], endValue[1]]
  }

  if (startValue && startValue.length >= 2 && !endValue) {
    return [startValue[0], startValue[1]]
  }

  if (startValue && startValue.length >= 2 && endValue && endValue.length >= 2) {
    const startDate = toDate(startValue[0])
    const endDate = toDate(startValue[1])
    const otherStartDate = toDate(endValue[0])
    const otherEndDate = toDate(endValue[1])

    if (![startDate, endDate, otherStartDate, otherEndDate].every(isValid)) {
      return ['Invalid date', 'Invalid date']
    }

    const earliestStart = compareAsc(startDate, otherStartDate) <= 0 ? startDate : otherStartDate
    const latestEnd = compareAsc(endDate, otherEndDate) >= 0 ? endDate : otherEndDate

    return [format(earliestStart, 'yyyy-MM-dd HH:mm:ss'), format(latestEnd, 'yyyy-MM-dd HH:mm:ss')]
  }

  return []
}

/**
 * Turns a given timespan value into a duration object.
 *
 * @param value Timespan value to convert to duration.
 * @returns A duration object from a given timespan or 'null' if the input is invalid.
 */
export function durationFromTimeSpan(value: string | null | undefined): Duration | null {
  if (value == null) return null
  const match = /^(-?)(?:(\d+)\.)?(\d+):(\d{2}):(\d{2})(?:\.(\d{1,7}))?$/.exec(value)
  if (!match) return null
  const sign = match[1] === '-' ? -1 : 1
  const days = Number.parseInt(match[2] ?? '0', 10)
  const hours = Number.parseInt(match[3], 10)
  const minutes = Number.parseInt(match[4], 10)
  const seconds = Number.parseInt(match[5], 10)
  const fractionalMilliseconds = Number.parseInt(
    (match[6] ?? '').padEnd(3, '0').slice(0, 3) || '0',
    10,
  )
  const totalMs =
    (days * 86400 + hours * 3600 + minutes * 60 + seconds) * 1000 + fractionalMilliseconds
  return {
    days: () => sign * days,
    hours: () => sign * hours,
    minutes: () => sign * minutes,
    seconds: () => sign * seconds,
    asMilliseconds: () => sign * totalMs,
    asHours: () => (sign * totalMs) / 3600000,
  }
}

/**
 * Turns a given number of milliseconds into a duration object.
 *
 * @param ms Number of milliseconds to convert to a duration object.
 * @returns Duration object from number of provided milliseconds.
 */
export function durationFromMs(ms: number): Duration {
  const abs = Math.abs(ms)
  const sign = ms < 0 ? -1 : 1
  const totalSeconds = Math.floor(abs / 1000)
  const totalMinutes = Math.floor(totalSeconds / 60)
  const totalHours = Math.floor(totalMinutes / 60)
  const days = Math.floor(totalHours / 24)
  const hours = totalHours % 24
  const minutes = totalMinutes % 60
  const seconds = totalSeconds % 60
  return {
    days: () => sign * days,
    hours: () => sign * hours,
    minutes: () => sign * minutes,
    seconds: () => sign * seconds,
    asMilliseconds: () => ms,
    asHours: () => ms / 3600000,
  }
}

/**
 * Converts a duration value and unit into milliseconds.
 *
 * @param value Whole or fractional duration value.
 * @param unit Duration unit for the provided value.
 * @returns Milliseconds represented by the provided duration.
 */
export function durationInMilliseconds(value: number, unit: DurationUnit): number {
  switch (unit) {
    case 'seconds':
      return value * 1000
    case 'minutes':
      return value * 60 * 1000
    case 'hours':
      return value * 60 * 60 * 1000
    case 'days':
      return value * 24 * 60 * 60 * 1000
  }
}
